import DownloadIcon from '@mui/icons-material/Download';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { Alert, Autocomplete, Button, Paper, Stack, TextField } from '@mui/material';
import type { ColDef } from 'ag-grid-community';
import { useMemo, useState } from 'react';
import { formatUIDateTime,  endOfDayIso, formatBoxPieces, startOfDayIso, toDateInput } from '@tiles-erp/shared';
import { usePagination } from '@tiles-erp/hooks';
import type { ProductItem, StockVerificationReportRow } from '@tiles-erp/shared-types';
import { DateField,  PageContainer } from '@tiles-erp/ui';
import { DataTable } from '../../components/DataTable';
import { SearchableSelect } from '../../components/SearchableSelect';
import { downloadTableExcel, downloadTablePdf, fetchAllListRows, type ExportColumn } from '../../components/ListExportButtons';
import { useSessionBranchId } from '../../lib/session-branch';
import { useCatalogOptions } from '../catalog/api';
import { useBranches } from '../products/branch-prices-api';
import { useProducts, useProductSizes } from '../products/api';
import {
  useGodowns,
  useStockVerificationFilterOptions,
  useStockVerificationReport,
  type StockVerificationFilters,
} from './api';

const daysAgo = (days: number): string => {
  const date = new Date();
  date.setDate(date.getDate() - days);
  return toDateInput(date);
};

const quantity = (value: number, row: StockVerificationReportRow): string =>
  formatBoxPieces(value, row.piecesPerBox, row.baseUom === 'PIECE');

const exportColumns: ExportColumn<StockVerificationReportRow>[] = [
  { header: 'Date & time', value: (row) => formatUIDateTime(new Date(row.checkedAt)), width: 120 },
  { header: 'Branch', value: (row) => row.branchName, width: 125 },
  { header: 'Godown', value: (row) => row.godownName, width: 110 },
  { header: 'SKU', value: (row) => row.sku, width: 75 },
  { header: 'Product', value: (row) => row.productName, width: 180 },
  { header: 'Brand', value: (row) => row.brandName, width: 90 },
  { header: 'Category', value: (row) => row.categoryName, width: 105 },
  { header: 'Size', value: (row) => row.sizeMm ?? '', width: 65 },
  { header: 'Existing stock', value: (row) => quantity(row.bookQtyBoxes, row), width: 90 },
  { header: 'Physical count', value: (row) => quantity(row.countedQtyBoxes, row), width: 90 },
  { header: 'Difference', value: (row) => quantity(row.differenceBoxes, row), width: 80 },
  { header: 'Verified by', value: (row) => row.checkedByName, width: 110 },
];

/** History of every physical count, including checks where no adjustment was needed. */
export function StockVerificationReportPage(): JSX.Element {
  const pagination = usePagination({ initialPageSize: 100 });
  const [branchId, setBranchId] = useSessionBranchId();
  const [godownId, setGodownId] = useState('');
  const [brandId, setBrandId] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [sizeMm, setSizeMm] = useState('');
  const [checkedBy, setCheckedBy] = useState('');
  const [result, setResult] = useState('');
  const [fromDate, setFromDate] = useState(() => daysAgo(30));
  const [toDate, setToDate] = useState(() => toDateInput(new Date()));
  const [productSearch, setProductSearch] = useState('');
  const [product, setProduct] = useState<ProductItem | null>(null);
  const [filters, setFilters] = useState<StockVerificationFilters>(() => ({
    branchId: branchId || undefined,
    fromDate: startOfDayIso(daysAgo(30)),
    toDate: endOfDayIso(toDateInput(new Date())),
  }));
  const [exporting, setExporting] = useState<'excel' | 'pdf' | null>(null);
  const [error, setError] = useState('');

  const branches = useBranches();
  const godowns = useGodowns(branchId || undefined);
  const brands = useCatalogOptions('/brands');
  const categories = useCatalogOptions('/categories', brandId || undefined);
  const sizes = useProductSizes();
  const staff = useStockVerificationFilterOptions();
  const products = useProducts(
    { page: 1, pageSize: 100, search: productSearch, sortOrder: 'asc' },
    { brandId: brandId || undefined, categoryId: categoryId || undefined, sizeMm: sizeMm || undefined },
  );
  const report = useStockVerificationReport(pagination.query, filters);

  const applyFilters = (): void => {
    setFilters({
      branchId: branchId || undefined,
      godownId: godownId || undefined,
      productId: product?.id,
      brandId: brandId || undefined,
      categoryId: categoryId || undefined,
      sizeMm: sizeMm || undefined,
      checkedBy: checkedBy || undefined,
      result: result || undefined,
      fromDate: fromDate ? startOfDayIso(fromDate) : undefined,
      toDate: toDate ? endOfDayIso(toDate) : undefined,
    });
    pagination.setPage(1);
  };

  const columns = useMemo<ColDef<StockVerificationReportRow>[]>(() => [
    { field: 'checkedAt', headerName: 'Date & time', minWidth: 170, valueFormatter: (p) => p.value ? formatUIDateTime(new Date(p.value as string)) : '' },
    { field: 'branchName', headerName: 'Branch', minWidth: 160 },
    { field: 'godownName', headerName: 'Godown', minWidth: 150 },
    { field: 'sku', headerName: 'SKU', minWidth: 110 },
    { field: 'productName', headerName: 'Product', minWidth: 230 },
    { field: 'brandName', headerName: 'Brand', minWidth: 140 },
    { field: 'categoryName', headerName: 'Category', minWidth: 150 },
    { field: 'sizeMm', headerName: 'Size', minWidth: 100 },
    { headerName: 'Existing stock', minWidth: 145, valueGetter: (p) => p.data ? quantity(p.data.bookQtyBoxes, p.data) : '' },
    { headerName: 'Physical count', minWidth: 145, valueGetter: (p) => p.data ? quantity(p.data.countedQtyBoxes, p.data) : '' },
    {
      headerName: 'Difference', minWidth: 130,
      valueGetter: (p) => p.data ? quantity(p.data.differenceBoxes, p.data) : '',
      cellStyle: (p) => ({ fontWeight: 700, color: p.data?.differenceBoxes ? '#c62828' : '#1e874b' }),
    },
    { field: 'checkedByName', headerName: 'Verified by', minWidth: 160 },
  ], []);

  const runExport = async (format: 'excel' | 'pdf'): Promise<void> => {
    setExporting(format);
    setError('');
    try {
      const rows = await fetchAllListRows<StockVerificationReportRow>('/stock/verification-report', {
        ...filters,
        search: pagination.query.search,
      });
      if (!rows.length) throw new Error('There are no verification records for these filters.');
      const filename = `physical-stock-verification-${fromDate || 'all'}-${toDate || 'all'}`;
      if (format === 'excel') downloadTableExcel(`${filename}.xls`, 'Physical stock verification', exportColumns, rows);
      else downloadTablePdf(`${filename}.pdf`, 'Physical stock verification', exportColumns, rows);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'The report could not be exported.');
    } finally {
      setExporting(null);
    }
  };

  return <PageContainer title="Physical stock verification report"
    subtitle="Compare the ERP stock at verification time with the physical count, difference, location and staff member."
    actions={<Stack direction="row" spacing={1}>
      <Button variant="outlined" startIcon={<DownloadIcon />} disabled={Boolean(exporting) || !report.data?.items.length}
        onClick={() => void runExport('excel')}>Excel</Button>
      <Button variant="outlined" startIcon={<PictureAsPdfIcon />} disabled={Boolean(exporting) || !report.data?.items.length}
        onClick={() => void runExport('pdf')}>PDF</Button>
    </Stack>}>
    <Stack spacing={1.5}>
      {error && <Alert severity="error" onClose={() => setError('')}>{error}</Alert>}
      {report.error && <Alert severity="error">{report.error.message}</Alert>}
      <Paper variant="outlined" sx={{ p: 1.5 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} useFlexGap flexWrap="wrap" alignItems="center">
          <DateField label="From date" type="date" size="small" value={fromDate} onChange={(e) => setFromDate(e.target.value)} slotProps={{ inputLabel: { shrink: true } }} />
          <DateField label="To date" type="date" size="small" value={toDate} onChange={(e) => setToDate(e.target.value)} slotProps={{ inputLabel: { shrink: true } }} />
          <SearchableSelect label="Branch" value={branchId} allLabel="All accessible branches"
            options={(branches.data ?? []).map((row) => ({ value: row.id, label: row.name }))}
            onChange={(value) => { setBranchId(value); setGodownId(''); }} width={220} />
          <SearchableSelect label="Godown" value={godownId} allLabel="All godowns"
            options={(godowns.data ?? []).map((row) => ({ value: row.id, label: row.name }))}
            onChange={setGodownId} disabled={!branchId} width={190} />
          <SearchableSelect label="Brand" value={brandId} allLabel="All brands"
            options={(brands.data ?? []).map((row) => ({ value: row.id, label: row.name }))}
            onChange={(value) => { setBrandId(value); setCategoryId(''); setProduct(null); }} width={190} />
          <SearchableSelect label="Category" value={categoryId} allLabel="All categories"
            options={(categories.data ?? []).map((row) => ({ value: row.id, label: row.name }))}
            onChange={(value) => { setCategoryId(value); setProduct(null); }} width={210} />
          <SearchableSelect label="Size" value={sizeMm} allLabel="All sizes"
            options={(sizes.data ?? []).map((value) => ({ value, label: value }))}
            onChange={(value) => { setSizeMm(value); setProduct(null); }} width={150} />
          <Autocomplete options={products.data?.items ?? []} value={product} inputValue={productSearch}
            onChange={(_, value) => setProduct(value)} onInputChange={(_, value) => setProductSearch(value)}
            filterOptions={(options) => options} isOptionEqualToValue={(a, b) => a.id === b.id}
            getOptionLabel={(row) => `${row.sku} · ${row.name}`}
            renderInput={(params) => <TextField {...params} label="Product" size="small" />}
            sx={{ width: { xs: '100%', md: 280 } }} />
          <SearchableSelect label="Verified by" value={checkedBy} allLabel="All staff"
            options={(staff.data?.staff ?? []).map((row) => ({ value: row.id, label: row.name }))}
            onChange={setCheckedBy} width={190} />
          <SearchableSelect label="Result" value={result} allLabel="All results"
            options={[{ value: 'MATCHED', label: 'Matched' }, { value: 'DIFFERENCE', label: 'Difference' }]}
            onChange={setResult} width={160} />
          <Button variant="contained" startIcon={<FilterAltIcon />} onClick={applyFilters}>Filter</Button>
        </Stack>
      </Paper>
      <DataTable rows={report.data?.items ?? []} columns={columns} meta={report.data?.meta}
        pagination={pagination} loading={report.isLoading || report.isFetching}
        searchPlaceholder="Search SKU or product name…" exportable={false} height={650} />
    </Stack>
  </PageContainer>;
}
